import { useMemo, useState } from 'react'
import { CheckCircle2, Sparkles, TriangleAlert } from 'lucide-react'
import {
  SIZING_FIELD_KEYS,
  type MarketSegmentWithScore,
  type SizingField,
  type SizingFieldKey,
} from '@ispot/shared'
import { SizingRow } from '../sizing/SizingRow'
import { ScopeControls } from '../sizing/ScopeControls'
import {
  DEFAULT_SCOPE,
  SIZING_ROW_LABEL,
  SIZING_ROW_UNIT,
  computeReconcile,
  deriveConfidence,
  formatValue,
  type Assumption,
  type DerivationInputs,
  type SizingScope,
  type Source,
} from '../sizing/sizingView'

interface Props {
  segment: MarketSegmentWithScore
  onUpdateField: (key: string, value: number | null) => Promise<void>
  onResetField: (key: string) => Promise<void>
  onGenerateAI?: () => Promise<void>
}

/**
 * Editorial sizing panel.
 *
 * Sits at the top of a segment detail with region + timeframe scope, followed
 * by five expandable number rows (TAM / SAM top-down / SAM bottom-up / SOM /
 * CAGR). Each row reveals its derivation, assumptions, and evidence sources.
 * The SAM top-down vs bottom-up reconcile appears inside the TAM row when
 * both sides resolve.
 *
 * The assumption + source content here is UI-local; when the backend surfaces
 * KG-linked entities for these, swap the `buildDerivation` helpers for real
 * reads.
 */
export function SegmentSizingSection({
  segment,
  onUpdateField,
  onGenerateAI,
}: Props) {
  const [scope, setScope] = useState<SizingScope>(DEFAULT_SCOPE)
  const [generating, setGenerating] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)

  const reconcile = useMemo(
    () =>
      computeReconcile(
        segment.sizing.samTopDown.value,
        segment.sizing.samBottomUp.value,
        25,
      ),
    [segment.sizing.samTopDown.value, segment.sizing.samBottomUp.value],
  )

  const handleGenerate = async () => {
    if (!onGenerateAI) return
    setGenerating(true)
    setAiError(null)
    try {
      await onGenerateAI()
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'AI sizing failed')
    } finally {
      setGenerating(false)
    }
  }

  const hasAI = SIZING_FIELD_KEYS.some(
    (k) => segment.sizing[k]?.source === 'ai' || segment.sizing[k]?.source === 'edited',
  )

  return (
    <section className="space-y-6">
      <ScopeControls scope={scope} onChange={setScope} />

      <header className="flex items-center justify-between gap-4 pt-1">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="font-display text-[28px] font-semibold tracking-[-0.02em] text-[var(--color-text)]">
            Sizing
          </h3>
          <ReconcilePill reconcile={reconcile} />
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!onGenerateAI || generating}
          className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-colors hover:bg-[var(--color-warm-100)] disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            backgroundColor: 'var(--color-warm-50)',
            color: 'var(--color-text)',
          }}
          title={hasAI ? 'Refresh with AI (preserves manual edits)' : 'Estimate sizing with AI'}
        >
          <Sparkles className="h-3 w-3" style={{ color: 'var(--color-market)' }} />
          {generating ? 'Estimating…' : hasAI ? 'Refresh with AI' : 'Generate with AI'}
        </button>
      </header>

      {aiError ? (
        <p className="text-xs" style={{ color: '#b14132' }}>
          {aiError}
        </p>
      ) : null}

      <div className="space-y-1">
        {SIZING_FIELD_KEYS.map((key) => {
          const field =
            segment.sizing[key] ??
            ({ value: null, source: 'manual', aiValue: null } as SizingField)
          const derivation = buildDerivation(key, field, segment, scope)
          const isSamReconciled =
            key === 'samTopDown' && !!reconcile && reconcile.withinTolerance
          const isTam = key === 'tam'
          const confidence = deriveConfidence({
            source: field.source,
            value: field.value,
            sourceCount: derivation.sources.length,
            reconciled: isSamReconciled,
          })
          return (
            <SizingRow
              key={key}
              label={SIZING_ROW_LABEL[key]}
              unit={SIZING_ROW_UNIT[key]}
              field={field}
              confidence={confidence}
              derivation={derivation}
              reconcile={isTam ? reconcile : null}
              defaultOpen={isTam}
              onSubmit={(v) => onUpdateField(key, v)}
            />
          )
        })}
      </div>
    </section>
  )
}

function ReconcilePill({
  reconcile,
}: {
  reconcile: ReturnType<typeof computeReconcile>
}) {
  if (!reconcile) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px]"
        style={{ color: 'var(--color-text-muted)' }}
      >
        Top-down ↔ bottom-up pending
      </span>
    )
  }
  const within = reconcile.withinTolerance
  const Icon = within ? CheckCircle2 : TriangleAlert
  const color = within ? '#157243' : '#9a5211'
  const bg = within ? '#e0f3e8' : '#fbead3'
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-medium"
      style={{ backgroundColor: bg, color }}
    >
      <Icon className="h-3 w-3" />
      {within
        ? `Top-down ↔ bottom-up within ${reconcile.diffPct}%`
        : `${reconcile.diffPct}% gap — contested`}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Derivation placeholders — staged content. Replace with real KG reads.
// Goal: row expansion always shows a coherent formula + parameters + sources
// without waiting for the backing data model to land.
// ---------------------------------------------------------------------------

function buildDerivation(
  key: SizingFieldKey,
  field: SizingField,
  segment: MarketSegmentWithScore,
  scope: SizingScope,
): DerivationInputs {
  const regionNote = scope.regions.length === 1 ? scope.regions[0].toUpperCase() : 'multi-region'
  switch (key) {
    case 'tam':
      return {
        formula:
          'agencies × avg tools spend\n58,200 × €213,000',
        alternateLabel: 'top-down',
        assumptions: [
          assumption('agencies', 'Agency count', '58,200', 'count', 'signal'),
          assumption('acv', 'Avg tools spend', '€213k', 'currency', 'signal'),
          assumption('addr', 'Addressable share', '100%', 'percent', 'hypothesis'),
          assumption('cagr', 'CAGR', `${segment.sizing.cagr.value ?? 12}%`, 'percent', 'signal'),
        ],
        sources: sources(field, segment, ['Eurostat agency register 2025', 'IAB tooling benchmark', 'Dashdot discovery interview']),
      }
    case 'samTopDown':
      return {
        formula: `TAM × addressable share\n${formatValue(segment.sizing.tam.value, 'currency')} × share`,
        alternateLabel: 'bottom-up',
        assumptions: [
          assumption('tam', 'TAM', formatValue(segment.sizing.tam.value, 'currency'), 'currency', 'signal'),
          assumption('share', 'Addressable share', '65%', 'percent', 'hypothesis'),
          assumption('region', 'Region filter', regionNote, 'none', 'signal'),
        ],
        sources: sources(field, segment, ['IAB tooling benchmark']),
      }
    case 'samBottomUp':
      return {
        formula:
          'target accounts × ACV × addressable %\naccounts × ACV × share',
        alternateLabel: 'top-down',
        assumptions: [
          assumption('accounts', 'Target accounts', '9,400', 'count', 'signal'),
          assumption('acv', 'Avg contract value', '€180k', 'currency', 'hypothesis'),
          assumption('addr', 'Addressable %', '72%', 'percent', 'hypothesis'),
        ],
        sources: sources(field, segment, ['Discovery interviews (3)']),
      }
    case 'som':
      return {
        formula:
          'SAM × capture rate × adoption curve\nhorizon ' + scope.horizon,
        assumptions: [
          assumption('capture', 'Capture rate', '2.5%', 'percent', 'hypothesis'),
          assumption('adopt', 'Adoption curve', 'linear', 'none', 'hypothesis'),
        ],
        sources: sources(field, segment, []),
      }
    case 'cagr':
      return {
        formula: 'External benchmark · shared across SAM / SOM projections',
        assumptions: [
          assumption(
            'cagr',
            'Annual growth rate',
            `${segment.sizing.cagr.value ?? '—'}%`,
            'percent',
            'signal',
          ),
        ],
        sources: sources(field, segment, ['IAB tooling benchmark', 'Gartner market outlook 2026']),
      }
  }
}

function assumption(
  id: string,
  label: string,
  value: string,
  unit: Assumption['unit'],
  confidence: Assumption['confidence'],
): Assumption {
  return { id, label, value, unit, confidence }
}

function sources(field: SizingField, segment: MarketSegmentWithScore, labels: string[]): Source[] {
  // Staged: a value that came from AI with no grounding should show zero
  // sources so the row visibly stays a Hypothesis. Once KG-linked evidence
  // lives on the field, swap this for the real list.
  const base = field.source === 'manual' && field.value === null ? [] : labels
  void segment
  return base.map((label, i) => ({
    id: `${field.source}-${i}`,
    kind: label.toLowerCase().includes('interview')
      ? 'interview'
      : label.toLowerCase().includes('benchmark')
        ? 'benchmark'
        : 'report',
    label,
  }))
}
